import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { DateInput } from '@/components/form/date-input';
import { Button } from '@/components/ui';
import { Modal } from '@/components/ui/modal';
import { formatBusinessDate, todayBusinessDate } from '@/utils/date-utils';

type AsOfChipProps = {
	asOfMs?: number;
	onOpen: () => void;
	onReset: () => void;
};

/**
 * Pill-shaped chip that surfaces the current `?asOf=` time-travel state.
 * Pressing opens the parent's modal; the inline reset shortcut clears the
 * override without going through the modal.
 */
export const AsOfChip: React.FC<AsOfChipProps> = ({ asOfMs, onOpen, onReset }) => (
	<Pressable
		onPress={onOpen}
		style={[styles.chip, asOfMs ? styles.chipActive : null]}
		accessibilityLabel="Change view-as-of date"
	>
		<Ionicons name="time-outline" size={14} color={asOfMs ? '#b45309' : '#546e7a'} />
		<Text style={[styles.chipLabel, asOfMs ? styles.chipLabelActive : null]}>
			{asOfMs ? `Viewing as of ${formatBusinessDate(asOfMs)}` : 'Viewing as of today'}
		</Text>
		{asOfMs ? (
			<Pressable onPress={onReset} hitSlop={8} style={styles.reset}>
				<Ionicons name="close-circle" size={16} color="#b45309" />
			</Pressable>
		) : null}
	</Pressable>
);

type AsOfModalProps = {
	visible: boolean;
	asOfMs?: number;
	title: string;
	helpText: string;
	onApply: (ms: number) => void;
	onReset: () => void;
	onDismiss: () => void;
};

/**
 * Companion picker for `<AsOfChip />`. Owns its own draft date so callers
 * just supply the current `asOfMs` and apply/reset/dismiss handlers.
 */
export const AsOfModal: React.FC<AsOfModalProps> = ({ visible, asOfMs, title, helpText, onApply, onReset, onDismiss }) => {
	const [draft, setDraft] = useState<Date | null>(() => (asOfMs ? new Date(asOfMs) : todayBusinessDate()));
	useEffect(() => {
		if (visible) setDraft(asOfMs ? new Date(asOfMs) : todayBusinessDate());
	}, [visible, asOfMs]);
	return (
		<Modal visible={visible} onDismiss={onDismiss} contentContainerStyle={styles.modal}>
			<Text style={styles.modalTitle}>{title}</Text>
			<Text style={styles.modalHelp}>{helpText}</Text>
			<View style={styles.modalField}>
				<DateInput label="As of" value={draft} onChange={setDraft} />
			</View>
			<View style={styles.modalActions}>
				{asOfMs ? (
					<Button mode="text" onPress={() => { onDismiss(); onReset(); }}>
						Reset to today
					</Button>
				) : null}
				<Button mode="outlined" onPress={onDismiss}>Cancel</Button>
				<Button mode="contained" onPress={() => onApply((draft ?? new Date()).getTime())} disabled={!draft}>
					Apply
				</Button>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	chip: {
		flexDirection: 'row', alignItems: 'center', gap: 6,
		paddingHorizontal: 10, paddingVertical: 6,
		borderRadius: 999, borderWidth: 1,
		borderColor: '#cfd8dc', backgroundColor: '#f5f7f8',
	},
	chipActive: { borderColor: '#fbbf24', backgroundColor: '#fef3c7' },
	chipLabel: { color: '#546e7a', fontSize: 12, fontWeight: '500' },
	chipLabelActive: { color: '#b45309' },
	reset: { marginLeft: 2 },
	modal: { width: 440, maxWidth: '95%' },
	modalTitle: { fontSize: 18, fontWeight: '700', color: '#212121', marginBottom: 8 },
	modalHelp: { color: '#78909c', fontSize: 13, marginBottom: 16 },
	modalField: { marginBottom: 16 },
	modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8 },
});
